import React, { useEffect, useState, Dispatch, SetStateAction } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { resetPassword } from "aws-amplify/auth";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { useValidateRecaptcha } from "@api";

import { usePartnerInfo, withController } from "@utils";

import { Button, Text, Notification, Content, Title } from "@ui/elements";
import { Input } from "@ui/forms";
import { Stack } from "@ui/layout";

import { OpenEmailButtons, useAuth } from "@components";

import i18n from "../../services/translation/i18n";

type ForgotPasswordFields = {
  email: string;
};

const ControlledInput = withController<ForgotPasswordFields>()(Input);

export const ForgotPassword = () => {
  const { t } = useTranslation();

  const formSchema = Yup.object().shape({
    email: Yup.string().required(t("messages.email-required")),
  });
  const [searchParams] = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { mutateAsync: validateRecaptcha, isPending: recaptchaLoading } =
    useValidateRecaptcha();
  const { handleSubmit, control, watch, setValue } =
    useForm<ForgotPasswordFields>({
      resolver: yupResolver(formSchema),
      defaultValues: {
        email: searchParams.get("email") || "",
      },
    });
  const [requested, setRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const authContext = useAuth();
  const partnerInfo = usePartnerInfo();

  useEffect(() => {
    if (authContext.isAuthenticated) {
      navigate("/");
    }
  }, [authContext.isAuthenticated, navigate]);

  const email = watch("email");

  const onSubmit = async (values: ForgotPasswordFields) => {
    setLoading(true);
    setError(null);

    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      setLoading(false);
      return;
    }

    const token = await executeRecaptcha("password_reset");
    const recaptchaResponse = await validateRecaptcha({ data: { token } });

    if (!recaptchaResponse.success) {
      setError(t("messages.unexpected-error"));
      setLoading(false);
      return;
    }

    try {
      await resetPassword({
        username: values.email,
        options: {
          clientMetadata: {
            locale: i18n.language,
            partnerId: process.env.REACT_APP_PARTNER_ID!,
          },
        },
      });
      setRequested(true);
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : t("messages.unexpected-error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const setEmailValue = (value: string) => setValue("email", value);

  if (requested) {
    return (
      <RequestedState
        email={email}
        setValue={setEmailValue}
        setRequested={setRequested}
        setError={setError}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Helmet>
        <title>
          {t("forgot-password")} - {partnerInfo.name}
        </title>
      </Helmet>
      <Stack direction="column" gap="xl">
        <Stack direction="column">
          <Title size={5}>{t("reset-your-password")}</Title>
          <Text variant="secondary">{t("reset-password-info")}</Text>
          <ControlledInput
            type="email"
            name="email"
            placeholder={t("labels.email")}
            control={control}
            autoComplete="username"
          />
          {error && <Notification color="danger">{error}</Notification>}
        </Stack>
        <Stack direction="column" gap="xs">
          <Button
            type="submit"
            isLoading={loading || recaptchaLoading}
            color="primary"
            disabled={loading || recaptchaLoading}
          >
            {t("Reset Password")}
          </Button>
          <Link to="/login">
            <Button as="link" isFullwidth variant="text">
              {t("labels.cancel")}
            </Button>
          </Link>
        </Stack>
      </Stack>
    </form>
  );
};

interface RequestedStateProps {
  email: string;
  setValue: (value: string) => void;
  setRequested: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

const RequestedState = ({
  email,
  setValue,
  setRequested,
  setError,
}: RequestedStateProps) => {
  const { t } = useTranslation();
  const [canResend, setCanResend] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const partnerInfo = usePartnerInfo();

  useEffect(() => {
    if (secondsLeft > 0) {
      const timer = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [secondsLeft]);

  const handleResend = () => {
    setValue("");
    setRequested(false);
    setError(null);
    setCanResend(false);
    setSecondsLeft(60);
  };

  return (
    <Stack direction="column" gap="xl">
      <Text variant="secondary">
        <Trans i18nKey="reset-password-instructions" values={{ email }} />
      </Text>
      <OpenEmailButtons />
      <Content size="small">
        <blockquote
          dangerouslySetInnerHTML={{
            __html: t("reset-password-list", {
              email: partnerInfo.contact.email,
            }),
          }}
        ></blockquote>
      </Content>

      <Stack direction="column" gap="xs" isFullwidth>
        <Link to="/login">
          <Button as="link" color="primary" isFullwidth>
            {t("labels.return-to-login")}
          </Button>
        </Link>
        <Button
          variant="text"
          isFullwidth
          onClick={handleResend}
          disabled={!canResend}
        >
          {t("labels.resend-email")}{" "}
          {!canResend && t("labels.wait-x-s", { seconds: secondsLeft })}
        </Button>
      </Stack>
    </Stack>
  );
};
